/**
 * SOLID Review
 * S — Strong. One use case: signing the tab out — the account forgotten in one step, then the token.
 * O — Strong. What a sign-out forgets is the Domain's decision.
 * L — Strong. Implements its use-case role exactly.
 * I — Strong. `execute`.
 * D — Strong. The decisions and auth-server ports and the session's step, injected.
 */
import type { IContextRequest } from '../ports/IContextRequest';
import type { ICommandNothing } from '../../domain/ports/schemas/TableMoves';
import type { TPortOutcome } from '../ports/PortResult';
import type { IUseCase } from '../uml-components/IUseCase';
import type { IConfigDepAuth } from './IConfigDepAuth';

const NOTHING: ICommandNothing = {};

/**
 * SIGNING OUT (FE3) — the welcome screen's and the profile's Log out, the entry when no player is behind the token, and a
 * deleted account. The client forgets the account in one step, then the token is forgotten — even when the step failed, and
 * the step's failure is the answer.
 */
export class SignOutUseCase implements IUseCase<ICommandNothing, TPortOutcome> {
  constructor(private readonly deps: IConfigDepAuth) {}

  async execute(ctx: IContextRequest, _command: ICommandNothing): Promise<TPortOutcome> {
    const { decisions, step, auth } = this.deps;
    const out = await step.run(ctx, (input) => decisions.signedOut(ctx, input, NOTHING));
    const forgotten = await auth.signOut(ctx);
    if (!out.success) return out;
    return step.outcome(forgotten);
  }
}
